import React, { useState, useEffect, useCallback } from 'react';
import TestCasesTable from './TestCasesTable';
import TestCaseUploadModal from './TestCaseUploadModal';
import ImportReportDialog from './import/ImportReportDialog';
import aiLogger from '../utils/aiLogger';

const TestCasesManager = ({ selectedProject, authenticatedUser, apiClient }) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter] = useState('all'); // Default to all test cases
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [importReport, setImportReport] = useState(null);

  // AI Logging: Component initialization
  useEffect(() => {
    aiLogger.component('TestCasesManager', 'Component Mounted', {
      projectId: selectedProject?.project_id,
      userId: authenticatedUser?.user_id,
      statusFilter
    });
  }, [selectedProject?.project_id, authenticatedUser?.user_id, statusFilter]);

  const fetchTestCases = useCallback(async ({ silent = false } = {}) => {
    if (!selectedProject?.project_id) return;
    
    try {
      if (!silent) setLoading(true);
      const data = await apiClient.getTestCases(selectedProject.project_id);
      aiLogger.debug('TestCasesManager API response:', data);
      aiLogger.debug('Response type:', typeof data, Array.isArray(data));
      
      let testCasesArray = [];
      
      if (Array.isArray(data)) {
        aiLogger.debug('Using array response, count:', data.length);
        testCasesArray = data;
      } else if (data && data.test_cases && Array.isArray(data.test_cases)) {
        aiLogger.debug('Using object response with test_cases, count:', data.test_cases.length);
        testCasesArray = data.test_cases;
      } else {
        aiLogger.debug('Using fallback, no test cases found');
        testCasesArray = [];
      }
      
      aiLogger.debug('Final test cases array:', testCasesArray);
      setTestCases(testCasesArray);
    } catch (error) {
      aiLogger.error('Failed to fetch test cases', error);
      setError('Failed to fetch test cases');
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.project_id, apiClient]);

  // Fetch test cases when component mounts or status filter changes
  useEffect(() => {
    if (selectedProject && authenticatedUser) {
      fetchTestCases();
    }
  }, [selectedProject, authenticatedUser, statusFilter, fetchTestCases]);

  const handleStatusChange = async (testCaseId, newStatus) => {
    const measureDuration = aiLogger.measurePerformance('updateTestCaseStatus');
    
    try {
      const result = await apiClient.updateTestCaseStatus(selectedProject.project_id, testCaseId, newStatus);
      
      // Update the local state - handle both id and test_id fields
      setTestCases(prev => 
        prev.map(tc => 
          (tc.id === testCaseId || tc.test_id === testCaseId)
            ? { ...tc, status: newStatus, updated_at: result.updated_at }
            : tc
        )
      );
      
      const duration = measureDuration();
      aiLogger.performance('Update Test Case Status', duration, {
        projectId: selectedProject.project_id,
        testCaseId,
        newStatus
      });
      
      aiLogger.userInteraction('update_test_case_status', null, {
        projectId: selectedProject.project_id,
        testCaseId,
        previousStatus: testCases.find(tc => tc.id === testCaseId || tc.test_id === testCaseId)?.status,
        newStatus
      });
      
      // Show success message
      aiLogger.success(`Test case ${newStatus === 'approved' ? 'approved' : 'status updated'} successfully`);
      
    } catch (err) {
      aiLogger.error('Failed to update test case status', {
        projectId: selectedProject?.project_id,
        testCaseId,
        newStatus,
        error: err.message
      }, err);
      
      // Show error to user (you might want to add a toast notification here)
      console.error('Failed to update test case status:', err);
    }
  };

  const handleRefresh = () => {
    aiLogger.userInteraction('refresh_test_cases', null, {
      projectId: selectedProject?.project_id,
      statusFilter
    });
    fetchTestCases();
  };

  const handleDeleteSelected = async (selectedNames) => {
    if (!selectedProject?.project_id) {
      return { success: false, deletedCount: 0, failedCount: 0, message: 'No project selected' };
    }
    // Map selected names to IDs from local state
    const toDelete = testCases.filter(tc => selectedNames.includes(tc.title || tc.name));
    const deletedIds = [];
    let failedCount = 0;

    for (const tc of toDelete) {
      const id = tc.test_id || tc.id;
      if (!id) {
        failedCount += 1;
        continue;
      }
      try {
        // Prefer SecureAPIClient method to ensure auth headers
        if (apiClient.deleteProjectTestCase) {
          await apiClient.deleteProjectTestCase(selectedProject.project_id, id);
        } else if (apiClient.deleteTestCase) {
          await apiClient.deleteTestCase(selectedProject.project_id, id);
        }
        deletedIds.push(id);
      } catch (err) {
        failedCount += 1;
        aiLogger.error('Failed to delete test case', err);
      }
    }

    // Optimistically remove successfully deleted rows so the table updates immediately
    if (deletedIds.length > 0) {
      setTestCases(prev => prev.filter(tc => !deletedIds.includes(tc.test_id || tc.id)));
    }

    // Confirm server state before reporting success; empty state appears only
    // after the refetch confirms the data is actually empty.
    try {
      await fetchTestCases({ silent: true });
    } catch (err) {
      aiLogger.error('Failed to refresh test cases after delete', err);
    }

    if (failedCount > 0) {
      return {
        success: false,
        deletedCount: deletedIds.length,
        failedCount,
        message: `Failed to delete ${failedCount} test case(s)${deletedIds.length ? ` (${deletedIds.length} deleted)` : ''}`,
      };
    }
    aiLogger.success(`Deleted ${deletedIds.length} test case(s)`);
    return {
      success: true,
      deletedCount: deletedIds.length,
      failedCount: 0,
      message: `Deleted ${deletedIds.length} test case(s)`,
    };
  };

  const handleApproveSelected = async (selectedNames) => {
    if (!selectedProject?.project_id) return;
    try {
      // Map selected names to IDs from local state
      const toApprove = testCases.filter(tc => selectedNames.includes(tc.title || tc.name));
      const testCaseIds = toApprove.map(tc => tc.test_id || tc.id).filter(id => id);
      
      if (testCaseIds.length > 0) {
        await apiClient.bulkApproveTestCases(selectedProject.project_id, testCaseIds);
        aiLogger.success(`Approved ${testCaseIds.length} test case(s)`);
        fetchTestCases();
      }
    } catch (err) {
      aiLogger.error('Failed to approve selected test cases', err);
    }
  };

  const handleUploadComplete = (result) => {
    aiLogger.userInteraction('test_case_upload_complete', null, {
      projectId: selectedProject?.project_id
    });
    // Refresh test cases after upload
    fetchTestCases();
    setUploadModalOpen(false);
    if (result && result.sheets) {
      try { localStorage.setItem('last_import_report', JSON.stringify(result)); } catch {}
      setImportReport(result);
    }
  };

  const handleOpenUpload = () => {
    aiLogger.userInteraction('open_test_case_upload', null, {
      projectId: selectedProject?.project_id
    });
    setUploadModalOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        color: '#6b7280'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading test cases...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '1rem',
        color: '#dc2626',
        marginBottom: '1rem'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0' }}>Error Loading Test Cases</h4>
        <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
        <button 
          onClick={handleRefresh}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '0.5rem 1rem',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Test Cases Table */}
      <TestCasesTable 
        testCases={testCases}
        totalCount={testCases.length}
        showApprovalWorkflow={false} // We're managing approved test cases here
        onStatusChange={handleStatusChange}
        onRefresh={handleRefresh}
        selectedProject={selectedProject}
        onOpenUpload={handleOpenUpload}
        onDeleteSelected={handleDeleteSelected}
        onApproveSelected={handleApproveSelected}
        onShowLastImport={() => {
          try {
            const raw = localStorage.getItem('last_import_report');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setImportReport(parsed);
          } catch {}
        }}
      />
      
      {/* Upload Modal */}
      <TestCaseUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        projectId={selectedProject?.project_id}
        onUploadComplete={handleUploadComplete}
      />

      {/* Import Report Dialog */}
      {importReport && (
        <ImportReportDialog
          open={!!importReport}
          onClose={() => setImportReport(null)}
          report={importReport}
        />
      )}
    </div>
  );
};

export default TestCasesManager; 